import { useEffect, useState } from 'react';

import { getFeatureFlagVariant, isFeatureEnabled, onFeatureFlags } from './client';

/**
 * Feature-flag facade. Default-off: `false` until a flag resolves true, and
 * whenever telemetry is unavailable. Re-renders when the flag set changes.
 */
export function useFeatureFlags(key: string): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => isFeatureEnabled(key));

  useEffect(() => {
    const update = () => setEnabled(isFeatureEnabled(key));
    update();
    return onFeatureFlags(update);
  }, [key]);

  return enabled;
}

/**
 * Variant facade for a multivariate flag: `null` until it resolves to a variant, and
 * whenever telemetry is unavailable (callers treat null as their default variant).
 * Re-renders when the flag set changes.
 */
export function useFeatureFlagVariant(key: string): string | null {
  const [variant, setVariant] = useState<string | null>(() => getFeatureFlagVariant(key));

  useEffect(() => {
    const update = () => setVariant(getFeatureFlagVariant(key));
    update();
    return onFeatureFlags(update);
  }, [key]);

  return variant;
}

/**
 * True once the flag store has delivered at least one resolution, or after
 * `graceMs` — whichever comes first. For surfaces that snapshot flags at
 * construction (the product tour freezes its step list when the driver is
 * built), mounting before the first resolution silently drops flag-gated
 * content; waiting with no bound would block the surface whenever telemetry
 * is unavailable, so the grace period caps the wait.
 */
export function useFeatureFlagsSettled(graceMs = 1500): boolean {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled) return undefined;
    const settle = () => setSettled(true);
    const timer = window.setTimeout(settle, graceMs);
    const unsubscribe = onFeatureFlags(settle);
    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, [graceMs, settled]);

  return settled;
}
